import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaLock, FaUpload, FaDownload, FaBan, FaEnvelope, FaLink,
  FaPlus, FaTrash, FaCopy, FaChartBar, FaUser,
  FaSignOutAlt, FaClock, FaFileAlt, FaInbox, FaPalette, FaCog
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('shares');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portals, setPortals] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchPortals();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard`);
      setDashboardData(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPortals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portals`);
      setPortals(response.data.portals || []);
    } catch (err) {
      console.error('Portals error:', err);
    }
  };

  const fetchSubmissions = async (portalId) => {
    try {
      const response = await axios.get(`${API_URL}/api/portals/${portalId}/submissions`);
      setSubmissions(response.data.submissions || []);
    } catch (err) {
      console.error('Submissions error:', err);
    }
  };

  const handleRevoke = async (fileId) => {
    if (!window.confirm('Are you sure you want to revoke access to this file?')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/revoke/${fileId}`);
      fetchDashboard();
    } catch (err) {
      alert('Failed to revoke file access');
    }
  };

  const handleCreatePortal = async () => {
    const name = prompt('Enter portal name:');
    if (!name) return;

    try {
      const response = await axios.post(`${API_URL}/api/portals`, { name });
      fetchPortals();
      alert(`Portal created! Link: ${response.data.portal.portalUrl}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create portal');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2186EB] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const files = dashboardData?.files || [];
  const dashboardPortals = dashboardData?.portals || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaLock className="text-[#2186EB] text-2xl" />
            <h1 className="text-2xl font-bold text-gray-900">MirageDocs</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/upload')}
              className="flex items-center gap-2 px-4 py-2 bg-[#FF7417] text-white rounded-lg font-semibold hover:bg-orange-600 transition"
            >
              <FaUpload /> New Share
            </button>
            <span className="text-sm text-gray-600">
              <FaUser className="inline mr-1" />
              {user?.email}
            </span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
              {user?.plan?.toUpperCase() || 'FREE'}
            </span>
            <button
              onClick={() => navigate('/account')}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Account Settings"
            >
              <FaCog />
            </button>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Shares</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeShares || 0}</p>
              </div>
              <FaFileAlt className="text-3xl text-[#2186EB]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Portals</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activePortals || 0}</p>
              </div>
              <FaInbox className="text-3xl text-[#FF7417]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">New Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.newSubmissions || 0}</p>
              </div>
              <FaEnvelope className="text-3xl text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plan</p>
                <p className="text-2xl font-bold text-gray-900">{user?.plan?.toUpperCase() || 'FREE'}</p>
                {user?.plan === 'free' && (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="text-xs text-[#2186EB] hover:underline mt-1"
                  >
                    Upgrade →
                  </button>
                )}
                {user?.plan !== 'free' && (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="text-xs text-gray-500 hover:underline mt-1"
                  >
                    Manage Plan →
                  </button>
                )}
              </div>
              <FaChartBar className="text-3xl text-purple-500" />
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        {stats.usage && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">Usage This Month</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">File Shares</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#2186EB] h-2 rounded-full"
                      style={{
                        width: stats.usage.sharesLimit === 'unlimited' ? '100%' :
                          `${Math.min(100, (stats.usage.sharesThisMonth / stats.usage.sharesLimit) * 100)}%`
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    {stats.usage.sharesThisMonth} / {stats.usage.sharesLimit}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">File Size Limit</p>
                <p className="text-sm font-medium">
                  {stats.usage.maxFileSizeMB}MB per file
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('shares')}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition ${
                  activeTab === 'shares'
                    ? 'border-[#2186EB] text-[#2186EB]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FaFileAlt className="inline mr-2" />
                My Shares ({files.length})
              </button>
              <button
                onClick={() => { setActiveTab('portals'); fetchPortals(); }}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition ${
                  activeTab === 'portals'
                    ? 'border-[#2186EB] text-[#2186EB]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FaInbox className="inline mr-2" />
                Intake Portals ({dashboardPortals.length})
              </button>
              <button
                onClick={() => setActiveTab('submissions')}
                className={`px-6 py-4 font-medium text-sm border-b-2 transition ${
                  activeTab === 'submissions'
                    ? 'border-[#2186EB] text-[#2186EB]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FaEnvelope className="inline mr-2" />
                Submissions
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Shares Tab */}
            {activeTab === 'shares' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Active File Shares</h3>
                  <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 bg-[#2186EB] text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    <FaUpload /> Upload New File
                  </button>
                </div>
                {files.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FaFileAlt className="text-4xl mx-auto mb-4 opacity-50" />
                    <p>No active file shares</p>
                    <button
                      onClick={() => navigate('/')}
                      className="mt-4 text-[#2186EB] hover:underline"
                    >
                      Upload your first file →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {files.map((file) => (
                      <div key={file.fileId} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{file.originalName}</h4>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              <span><FaClock className="inline mr-1" /> Expires: {new Date(file.expiresAt).toLocaleString()}</span>
                              <span><FaDownload className="inline mr-1" /> {file.downloadCount} / {file.maxDownloads} downloads</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleRevoke(file.fileId)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Revoke access"
                            >
                              <FaBan />
                            </button>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-400">
                          Share link not shown here: for your privacy, MirageDocs never stores the
                          decryption key, so only the browser that created this share has the full link.
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Portals Tab */}
            {activeTab === 'portals' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Intake Portals</h3>
                  <button
                    onClick={handleCreatePortal}
                    className="px-4 py-2 bg-[#FF7417] text-white rounded-lg hover:bg-orange-600 transition flex items-center gap-2"
                  >
                    <FaPlus /> Create Portal
                  </button>
                </div>
                {dashboardPortals.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FaInbox className="text-4xl mx-auto mb-4 opacity-50" />
                    <p>No intake portals yet</p>
                    <button
                      onClick={handleCreatePortal}
                      className="mt-4 text-[#FF7417] hover:underline"
                    >
                      Create your first portal →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dashboardPortals.map((portal) => (
                      <div key={portal.portalId} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{portal.name}</h4>
                            {portal.description && (
                              <p className="text-sm text-gray-600 mt-1">{portal.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              <span><FaInbox className="inline mr-1" /> {portal.submissionCount} submissions</span>
                              {portal.newSubmissions > 0 && (
                                <span className="text-green-600 font-medium">
                                  {portal.newSubmissions} new
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => copyToClipboard(portal.portalUrl)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                              title="Copy portal link"
                            >
                              <FaCopy />
                            </button>
                            <button
                              onClick={() => { setActiveTab('submissions'); fetchSubmissions(portal.portalId); }}
                              className="px-3 py-1 bg-[#2186EB] text-white rounded text-sm hover:bg-blue-700"
                            >
                              View Submissions
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          <FaLink className="inline mr-1" />
                          {portal.portalUrl}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Submissions Tab */}
            {activeTab === 'submissions' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Submissions</h3>
                {submissions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FaEnvelope className="text-4xl mx-auto mb-4 opacity-50" />
                    <p>No submissions yet</p>
                    <p className="text-sm mt-2">Submissions will appear here when files are uploaded to your portals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {submissions.map((submission) => (
                      <div key={submission.submissionId} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{submission.originalName}</h4>
                            {submission.submitterName && (
                              <p className="text-sm text-gray-600">From: {submission.submitterName}</p>
                            )}
                            {submission.submitterEmail && (
                              <p className="text-sm text-gray-600">{submission.submitterEmail}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Submitted: {new Date(submission.submittedAt).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            {submission.isNew && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold mr-2">
                                NEW
                              </span>
                            )}
                            <a
                              href={`${API_URL}/api/download/${submission.fileId}`}
                              className="px-3 py-1 bg-[#2186EB] text-white rounded text-sm hover:bg-blue-700 inline-block"
                            >
                              <FaDownload className="inline mr-1" /> Download
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

